import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { SelectProps } from '@amzn/awsui-components-react';
import { getFeatureFlags } from 'src/services/FeatureFlagsService';
import { isValidReportSelection } from 'src/models/reports/ReportPeriod';
import MessageService from 'src/services/utils/MessageService';

/** Shown when a report deep link's accounting period or day is missing / malformed. */
export const INVALID_REPORT_SELECTION_MESSAGE =
    'That report link is invalid — its accounting period or day is missing or malformed. '
    + 'Please select a report from the dashboard.';

/**
 * The accounting period + day a report page inherits from the Reports dashboard when the
 * eventDrivenTpTaxReportingEnabled flag is on. With the flag on, the report pages no longer render their
 * own period / accounting-day dropdowns — the user picks those on the dashboard, which navigates here
 * with `?period=<MMM-YY>&accountingDay=<7|11|ONDEMAND>` in the URL.
 */
export interface DashboardReportContext {
    /** True once the feature-flag lookup has resolved (avoids acting on the default-false flag). */
    flagResolved: boolean;
    /** Whether the event-driven TP Tax reporting redesign is enabled. */
    eventDrivenTpTaxReportingEnabled: boolean;
    /**
     * Whether report currency display is enabled (the reportCurrencyOverlay AppConfig flag). The
     * multi-CLI (LEF/CWB) views use this to gate the per-CLI FRAMES currency annotations. Shared here
     * so both views read the flag from one place instead of each fetching it independently.
     */
    reportCurrencyOverlayEnabled: boolean;
    /** Reporting period (MMM-YY) read from the URL query, or null when absent/invalid. */
    period: string | null;
    /** Accounting day (7 / 11 / ONDEMAND) read from the URL query, or null when absent/invalid. */
    accountingDay: string | null;
    /**
     * The inherited period as a ready-to-use Select option ({@code {label, value}}), or null when there
     * is no valid selection. Exposed so the report views seed their period dropdown directly from the hook
     * instead of each re-building the option object (the error-prone part). Same for {@link accountingDayOption}.
     */
    periodOption: SelectProps.Option | null;
    /** The inherited accounting day as a ready-to-use Select option, or null when there is no valid selection. */
    accountingDayOption: SelectProps.Option | null;
}

/**
 * Shared hook for the four TP Tax report pages (MAR / CWB / LEF / VAR) so they inherit the dashboard's
 * period + accounting-day selection instead of offering their own dropdowns.
 *
 * <p>Behavior when the flag is ON: read period + accountingDay from the URL query string. If either is
 * missing OR malformed (a direct link / bookmark / hard refresh / hand-edited or stale deep link with no
 * valid dashboard selection to fall back on), redirect to the Reports dashboard ({@code /reports}) — there
 * are no on-page dropdowns to pick them, and passing garbage to the report fetch only yields a cryptic
 * error. When the flag is OFF the hook is inert (period / accountingDay reported as null, no redirect) so
 * the legacy dropdown-driven pages are unaffected.
 *
 * <p>Callers should render the flag-on (dropdown-free) layout only once {@code flagResolved} is true and
 * both {@code period} and {@code accountingDay} are present (non-null ⇒ valid); otherwise the redirect is
 * in flight.
 */
export function useDashboardReportContext(messageService?: MessageService): DashboardReportContext {
    const history = useHistory();
    const location = useLocation();

    const [flagResolved, setFlagResolved] = useState(false);
    const [eventDrivenTpTaxReportingEnabled, setEventDrivenTpTaxReportingEnabled] = useState(false);
    const [reportCurrencyOverlayEnabled, setReportCurrencyOverlayEnabled] = useState(false);

    useEffect(() => {
        getFeatureFlags()
            .then((flags) => {
                setEventDrivenTpTaxReportingEnabled(flags.eventDrivenTpTaxReportingEnabled);
                setReportCurrencyOverlayEnabled(flags.reportCurrencyOverlayEnabled);
                setFlagResolved(true);
            })
            .catch((err) => {
                // On a flag-lookup failure, fall back to the legacy (flag-off) behavior so the page still
                // renders its own dropdowns rather than getting stuck.
                console.error('Failed to load feature flags', err);
                setFlagResolved(true);
            });
    }, []);

    const params = new URLSearchParams(location.search);
    const period = params.get('period');
    const accountingDay = params.get('accountingDay');
    // Accept only a well-formed selection. A malformed period/day (hand-edited or stale deep link) is
    // treated like a missing one — redirected below rather than passed on to a doomed report fetch that
    // would surface a cryptic transport-level error.
    const selectionValid = isValidReportSelection(period, accountingDay);

    // Flag on but the URL lacks a valid dashboard selection → nothing to render (no dropdowns), so send
    // the user back to the dashboard to pick a period + version.
    useEffect(() => {
        if (flagResolved && eventDrivenTpTaxReportingEnabled && !selectionValid) {
            // Explain the bounce instead of silently sending the user back. The banner is dispatched to
            // the global store, so it survives the redirect and shows on the dashboard the user lands on.
            messageService?.showErrorAutoDismissBanner(INVALID_REPORT_SELECTION_MESSAGE);
            history.replace('/reports');
        }
    }, [flagResolved, eventDrivenTpTaxReportingEnabled, selectionValid]);

    // Surface the values only when the redesign is on AND they are valid, so consumers never seed the
    // report pages with garbage.
    const inherit = eventDrivenTpTaxReportingEnabled && selectionValid;
    const inheritedPeriod = inherit ? period : null;
    const inheritedAccountingDay = inherit ? accountingDay : null;

    // A period/day value is always usable as its own Select label + value, so the option is just
    // {label: value, value}. Pre-built here so every report view seeds from one place — and memoized so
    // the object identity is stable across renders. Without memoization these fresh literals would change
    // identity every render, re-firing the consumers' seeding effect (keyed on them) in an infinite loop.
    const periodOption = useMemo(
        () => (inheritedPeriod ? { label: inheritedPeriod, value: inheritedPeriod } : null),
        [inheritedPeriod],
    );
    const accountingDayOption = useMemo(
        () => (inheritedAccountingDay ? { label: inheritedAccountingDay, value: inheritedAccountingDay } : null),
        [inheritedAccountingDay],
    );

    return {
        flagResolved,
        eventDrivenTpTaxReportingEnabled,
        reportCurrencyOverlayEnabled,
        period: inheritedPeriod,
        accountingDay: inheritedAccountingDay,
        periodOption,
        accountingDayOption,
    };
}
